import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import SydSelect from '../../../../commonDesign/SydSelect'
import SydInput from '../../../../commonDesign/SydInput'
import SydCheckbox from '../../../../commonDesign/SydCheckbox'
import SydLabel from '../../../../commonDesign/SydLabel'
import FadeIn from '../../../../molecules/Transitions/FadeIn'
import { DAYS_OF_WEEK, ORDINALS, MONTHS_OF_YEAR } from './redtailUIHelpers'

const BASE_TYPE_OPTIONS = [
  { label: 'Daily', value: 'daily' },
  { label: 'Weekly', value: 'weekly' },
  { label: 'Monthly', value: 'monthly' },
  { label: 'Yearly', value: 'yearly' }
]

const WEEKDAY_LABELS = {
  sunday: 'Su',
  monday: 'Mo',
  tuesday: 'Tu',
  wednesday: 'We',
  thursday: 'Th',
  friday: 'Fr',
  saturday: 'Sa'
}

const WEEKDAY_OPTIONS = DAYS_OF_WEEK.map(d => ({
  label: d.charAt(0).toUpperCase() + d.slice(1),
  value: d
}))

const MONTH_LABELS = MONTHS_OF_YEAR.map(m => ({
  short: m.charAt(0).toUpperCase() + m.slice(1, 3),
  value: m
}))

const bladeStyle = {
  flex: '0 0 320px',
  minWidth: '425px',
  background: 'var(--color-surface-200)',
  borderRadius: 'var(--border-radius-sm)',
  padding: '16px',
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  overflowY: 'auto',
  fontWeight: 'normal'
}

const toggleBtnBase = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: '36px',
  height: '32px',
  padding: '0 8px',
  borderRadius: 'var(--border-radius-sm)',
  border: 'var(--border-neutral-thin)',
  cursor: 'pointer',
  fontSize: 'var(--font-size-xs)',
  userSelect: 'none',
  transition: 'background 0.15s, color 0.15s'
}

const toggleBtnActive = {
  ...toggleBtnBase,
  background: 'var(--color-primary-100)',
  color: 'var(--color-contrast-primary-100)',
  borderColor: 'var(--color-primary-100)'
}

const toggleBtnInactive = {
  ...toggleBtnBase,
  background: 'var(--color-surface-100)',
  color: 'inherit'
}

function ToggleButton ({ active, onClick, children, style }) {
  return (
    <button
      type='button'
      style={{ ...(active ? toggleBtnActive : toggleBtnInactive), ...style }}
      onClick={onClick}
    >
      {children}
    </button>
  )
}

ToggleButton.propTypes = {
  active: PropTypes.bool,
  onClick: PropTypes.func,
  children: PropTypes.node,
  style: PropTypes.object
}

function DailyForm ({ value, onChange }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <span style={{ fontSize: 'var(--font-size-sm)' }}>Every</span>
      <SydInput
        type='number'
        min={1}
        minWidth='0px'
        value={value.repeats_every_n_days ?? ''}
        onChange={(e) => onChange({ ...value, repeats_every_n_days: e.target.value === '' ? '' : Number(e.target.value) })}
        onBlur={(e) => { if (!e.target.value || Number(e.target.value) < 1) onChange({ ...value, repeats_every_n_days: 1 }) }}
        style={{ width: '64px' }}
      />
      <span style={{ fontSize: 'var(--font-size-sm)' }}>day(s)</span>
    </div>
  )
}

DailyForm.propTypes = { value: PropTypes.object, onChange: PropTypes.func }

function WeeklyForm ({ value, onChange }) {
  const selectedDays = useMemo(
    () => value.repeats_weekly_each_days_of_the_week || [],
    [value.repeats_weekly_each_days_of_the_week]
  )

  const toggleDay = useCallback((day) => {
    const next = selectedDays.includes(day)
      ? selectedDays.filter(d => d !== day)
      : [...selectedDays, day]
    if (next.length === 0) return
    onChange({ ...value, repeats_weekly_each_days_of_the_week: next })
  }, [selectedDays, value, onChange])

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span style={{ fontSize: 'var(--font-size-sm)' }}>Every</span>
        <SydInput
          type='number'
          min={1}
          minWidth='0px'
          value={value.repeats_every_n_weeks ?? ''}
          onChange={(e) => onChange({ ...value, repeats_every_n_weeks: e.target.value === '' ? '' : Number(e.target.value) })}
          onBlur={(e) => { if (!e.target.value || Number(e.target.value) < 1) onChange({ ...value, repeats_every_n_weeks: 1 }) }}
          style={{ width: '64px' }}
        />
        <span style={{ fontSize: 'var(--font-size-sm)' }}>week(s)</span>
      </div>
      <div style={{ display: 'flex', gap: '4px', alignItems: 'center', flexWrap: 'wrap' }}>
        <span className='mr-2'>on</span>
        {DAYS_OF_WEEK.map(day => (
          <ToggleButton
            key={day}
            active={selectedDays.includes(day)}
            onClick={() => toggleDay(day)}
          >
            {WEEKDAY_LABELS[day]}
          </ToggleButton>
        ))}
      </div>
    </div>
  )
}

WeeklyForm.propTypes = { value: PropTypes.object, onChange: PropTypes.func }

function MonthlyForm ({ value, onChange }) {
  const mode = value.repeats_monthly || 'on'

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span style={{ fontSize: 'var(--font-size-sm)' }}>Every</span>
        <SydInput
          type='number'
          min={1}
          minWidth='0px'
          value={value.repeats_every_n_months ?? ''}
          onChange={(e) => onChange({ ...value, repeats_every_n_months: e.target.value === '' ? '' : Number(e.target.value) })}
          onBlur={(e) => { if (!e.target.value || Number(e.target.value) < 1) onChange({ ...value, repeats_every_n_months: 1 }) }}
          style={{ width: '64px' }}
        />
        <span style={{ fontSize: 'var(--font-size-sm)' }}>month(s)</span>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: 'var(--font-size-sm)' }}>
          <input
            type='radio'
            checked={mode === 'on'}
            onChange={() => onChange({ ...value, repeats_monthly: 'on' })}
          />
          On the
        </label>
        {mode === 'on' ? (
          <div style={{ display: 'flex', gap: '8px', paddingLeft: '22px', flexWrap: 'wrap' }}>
            <SydSelect
              minWidth={90}
              options={ORDINALS}
              value={(value.repeats_monthly_on_ordinals || [])[0] ?? 1}
              onChange={(e) => onChange({ ...value, repeats_monthly_on_ordinals: [Number(e.target.value)] })}
            />
            <SydSelect
              minWidth={110}
              options={WEEKDAY_OPTIONS}
              value={(value.repeats_monthly_on_days_of_the_week || [])[0] ?? 'monday'}
              onChange={(e) => onChange({ ...value, repeats_monthly_on_days_of_the_week: [e.target.value] })}
            />
          </div>
        ) : null}

        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: 'var(--font-size-sm)' }}>
          <input
            type='radio'
            checked={mode === 'each'}
            onChange={() => onChange({ ...value, repeats_monthly: 'each' })}
          />
          Each day of month
        </label>
        {mode === 'each' ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px', paddingLeft: '22px' }}>
            {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
              <ToggleButton
                key={day}
                active={(value.repeats_monthly_each_days_of_the_month || []).includes(day)}
                onClick={() => {
                  const current = value.repeats_monthly_each_days_of_the_month || []
                  const next = current.includes(day)
                    ? current.filter(d => d !== day)
                    : [...current, day].sort((a, b) => a - b)
                  if (next.length === 0) return
                  onChange({ ...value, repeats_monthly_each_days_of_the_month: next })
                }}
                style={{ minWidth: '30px', height: '26px', fontSize: '11px' }}
              >
                {day}
              </ToggleButton>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  )
}

MonthlyForm.propTypes = { value: PropTypes.object, onChange: PropTypes.func }

function YearlyForm ({ value, onChange }) {
  const selectedMonths = useMemo(
    () => value.repeats_yearly_each_months_of_the_year || [],
    [value.repeats_yearly_each_months_of_the_year]
  )
  const hasOn = !!value.repeats_yearly_on

  const toggleMonth = useCallback((month) => {
    const next = selectedMonths.includes(month)
      ? selectedMonths.filter(m => m !== month)
      : [...selectedMonths, month]
    if (next.length === 0) return
    onChange({ ...value, repeats_yearly_each_months_of_the_year: next })
  }, [selectedMonths, value, onChange])

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span style={{ fontSize: 'var(--font-size-sm)' }}>Every</span>
        <SydInput
          type='number'
          min={1}
          minWidth='0px'
          value={value.repeats_every_n_years ?? ''}
          onChange={(e) => onChange({ ...value, repeats_every_n_years: e.target.value === '' ? '' : Number(e.target.value) })}
          onBlur={(e) => { if (!e.target.value || Number(e.target.value) < 1) onChange({ ...value, repeats_every_n_years: 1 }) }}
          style={{ width: '64px' }}
        />
        <span style={{ fontSize: 'var(--font-size-sm)' }}>year(s) in</span>
      </div>

      <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
        {MONTH_LABELS.map(({ short, value: mv }) => (
          <ToggleButton
            key={mv}
            active={selectedMonths.includes(mv)}
            onClick={() => toggleMonth(mv)}
          >
            {short}
          </ToggleButton>
        ))}
      </div>

      <SydCheckbox
        label='On a specific weekday'
        size='small'
        value={hasOn}
        onChange={(_e, checked) => onChange({ ...value, repeats_yearly_on: checked })}
      />

      {hasOn ? (
        <div style={{ display: 'flex', gap: '8px', paddingLeft: '22px', flexWrap: 'wrap' }}>
          <SydSelect
            minWidth={90}
            options={ORDINALS}
            value={(value.repeats_yearly_on_ordinals || [])[0] ?? 1}
            onChange={(e) => onChange({ ...value, repeats_yearly_on_ordinals: [Number(e.target.value)] })}
          />
          <SydSelect
            minWidth={110}
            options={WEEKDAY_OPTIONS}
            value={(value.repeats_yearly_on_days_of_the_week || [])[0] ?? 'monday'}
            onChange={(e) => onChange({ ...value, repeats_yearly_on_days_of_the_week: [e.target.value] })}
          />
        </div>
      ) : null}
    </div>
  )
}

YearlyForm.propTypes = { value: PropTypes.object, onChange: PropTypes.func }

function EndCondition ({ value, onChange }) {
  return (
    <div style={{ borderTop: 'var(--border-neutral-thin)', paddingTop: '12px' }}>
      <SydLabel label='Ends' style={{ marginBottom: 0 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <SydSelect
            minWidth={100}
            options={[{ label: 'Never', value: 'never' }, { label: 'On date', value: 'on' }]}
            value={value.repeat_ends || 'never'}
            onChange={(e) => onChange({ ...value, repeat_ends: e.target.value })}
          />
          {value.repeat_ends === 'on' ? (
            <SydInput
              type='date'
              value={value.repeat_ends_on || ''}
              onChange={(e) => onChange({ ...value, repeat_ends_on: e.target.value })}
            />
          ) : null}
        </div>
      </SydLabel>
    </div>
  )
}

EndCondition.propTypes = { value: PropTypes.object, onChange: PropTypes.func }

function RecurrenceEditor ({ value, onChange, open }) {
  const baseType = value?.baseType || 'daily'

  const handleBaseTypeChange = useCallback((e) => {
    onChange({ ...value, baseType: e.target.value })
  }, [value, onChange])

  if (!open) return null

  return (
    <FadeIn style={bladeStyle}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, whiteSpace: 'nowrap' }}>Recurrence</span>
        <SydSelect
          minWidth={100}
          options={BASE_TYPE_OPTIONS}
          value={baseType}
          onChange={handleBaseTypeChange}
        />
      </div>

      {baseType === 'daily' ? <DailyForm value={value} onChange={onChange} /> : null}
      {baseType === 'weekly' ? <WeeklyForm value={value} onChange={onChange} /> : null}
      {baseType === 'monthly' ? <MonthlyForm value={value} onChange={onChange} /> : null}
      {baseType === 'yearly' ? <YearlyForm value={value} onChange={onChange} /> : null}

      <EndCondition value={value} onChange={onChange} />
    </FadeIn>
  )
}

RecurrenceEditor.propTypes = {
  value: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  open: PropTypes.bool
}

export default RecurrenceEditor
